import { useState } from 'react';
import { CheckCircle, Copy, Crown, DoorOpen, Robot, Users, X } from '@phosphor-icons/react';
import type { AiStrategy } from '../game/types';
import { listStartingCities } from '../game/startingCities';
import type { RoomSnapshot } from '../net/roomClient';
import { money } from '../ui/format';

interface RoomModalProps {
  room: RoomSnapshot | null;
  onCreate: (name: string) => Promise<void>;
  onJoin: (roomId: string, name: string) => Promise<void>;
  onLeave: () => Promise<void>;
  onUpdateSettings: (settings: { aiCount: number; aiDifficulty: AiStrategy }) => Promise<void>;
  onChooseCity: (city: string) => Promise<void>;
  onSetReady: (ready: boolean) => Promise<void>;
  onStartGame: () => Promise<void>;
  onClose: () => void;
}

const AI_DIFFICULTY_LABELS: Record<AiStrategy, string> = {
  basic: '普通',
  advanced: '进阶',
  strategic: '策略',
  elite: '精英',
};

const STARTING_CITIES = listStartingCities();

export function RoomModal({ room, onCreate, onJoin, onLeave, onUpdateSettings, onChooseCity, onSetReady, onStartGame, onClose }: RoomModalProps) {
  const [name, setName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setBusy(false);
    }
  };

  const copyRoomCode = async () => {
    if (!room) return;
    try {
      await navigator.clipboard.writeText(room.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const inLobby = room?.phase === 'lobby';
  const isOwner = !!room && room.me?.id === room.ownerId;
  const readyCount = room?.players.filter((player) => player.ready).length ?? 0;
  const allReady = !!room && room.players.length > 0 && room.players.every((player) => player.ready);
  const myCity = room?.me?.chosenCity ?? null;

  const header = !room
    ? { title: '创建或加入房间', sub: '和朋友在同一个市场里各自经营一家航空公司，互相竞争。' }
    : inLobby
      ? { title: `房间 ${room.id} · 准备阶段`, sub: '盲选开局城市并点击准备，所有人准备好后由房主开始游戏。' }
      : { title: `房间 ${room.id}`, sub: '所有玩家点击“下一周”后，服务端统一推进经营周。' };

  return (
    <div className="modal-backdrop" role="presentation" onClick={inLobby ? undefined : onClose}>
      <section className={`confirm-sheet room-sheet ${inLobby ? 'room-sheet-lobby' : ''}`} role="dialog" aria-modal="true" aria-labelledby="room-title" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <header className="confirm-header">
          <div>
            <span className="eyebrow">联机模式</span>
            <h2 id="room-title">{header.title}</h2>
            <p>{header.sub}</p>
          </div>
          {!inLobby && <button className="icon-button" onClick={onClose} aria-label="关闭"><X weight="bold" size={22} /></button>}
        </header>

        {!room && (
          <div className="room-form">
            <label className="room-field">
              <span>公司 / 玩家名称</span>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：南方之翼" maxLength={20} />
            </label>
            <button className="primary-button" disabled={busy} onClick={() => void run(() => onCreate(name))}>
              <Users weight="bold" size={16} /> 创建新房间
            </button>
            <div className="room-divider"><span>或加入朋友的房间</span></div>
            <label className="room-field">
              <span>房间号</span>
              <input
                value={roomCode}
                onChange={(event) => setRoomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="6 位房间号"
                maxLength={6}
              />
            </label>
            <button className="primary-button" disabled={busy || roomCode.length !== 6} onClick={() => void run(() => onJoin(roomCode, name))}>
              加入房间
            </button>
            {error && <p className="room-error">{error}</p>}
          </div>
        )}

        {room && (
          <div className="room-lobby">
            <button className="room-code-card" onClick={() => void copyRoomCode()} title="复制房间号">
              <span>房间号（点击复制，分享给朋友）</span>
              <strong>{room.id}</strong>
              <em><Copy weight="bold" size={14} /> {copied ? '已复制' : '复制'}</em>
            </button>

            {inLobby && (
              <div className="room-settings-card">
                <div className="room-settings-title"><Robot weight="bold" size={15} /> AI 竞争者（开局后锁定，不可再调整）</div>
                {isOwner ? (
                  <div className="room-settings-controls">
                    <label className="room-field">
                      <span>AI 数量</span>
                      <select
                        value={room.settings.aiCount}
                        disabled={busy}
                        onChange={(event) => void run(() => onUpdateSettings({ ...room.settings, aiCount: Number(event.target.value) }))}
                      >
                        {[0, 1, 2, 3, 4].map((count) => <option key={count} value={count}>{count === 0 ? '不加 AI' : `${count} 家`}</option>)}
                      </select>
                    </label>
                    <label className="room-field">
                      <span>AI 难度</span>
                      <select
                        value={room.settings.aiDifficulty}
                        disabled={busy || room.settings.aiCount === 0}
                        onChange={(event) => void run(() => onUpdateSettings({ ...room.settings, aiDifficulty: event.target.value as AiStrategy }))}
                      >
                        {(Object.keys(AI_DIFFICULTY_LABELS) as AiStrategy[]).map((key) => <option key={key} value={key}>{AI_DIFFICULTY_LABELS[key]}</option>)}
                      </select>
                    </label>
                  </div>
                ) : (
                  <p className="room-settings-readonly">
                    {room.settings.aiCount === 0
                      ? '房主没有加入 AI'
                      : `${room.settings.aiCount} 家 AI · ${AI_DIFFICULTY_LABELS[room.settings.aiDifficulty]}难度`}
                  </p>
                )}
              </div>
            )}

            {inLobby && (
              <div className="room-city-picker">
                <div className="room-settings-title">选择开局城市（互相盲选，开局后公开）</div>
                <div className="room-city-grid">
                  {STARTING_CITIES.map((city) => (
                    <button
                      key={city.iata}
                      className={`room-city-option ${myCity === city.iata ? 'selected' : ''}`}
                      disabled={busy}
                      onClick={() => void run(() => onChooseCity(city.iata))}
                    >
                      <strong>{city.nameZh}</strong>
                      <span>{city.iata} · {city.country}</span>
                      <small>市场 {city.market} · 商务 {city.business} · 旅游 {city.tourism}</small>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="room-status-line">
              <span className={`room-phase-pill ${room.phase}`}>
                {room.phase === 'lobby' ? '准备阶段' : room.phase === 'playing' ? '经营进行中' : '服务端结算中'}
              </span>
              <span>
                {inLobby
                  ? `${readyCount}/${room.players.length} 已准备`
                  : `第 ${room.week} 周 · ${readyCount}/${room.players.length} 已提交`}
              </span>
            </div>

            <ul className="room-player-list">
              {room.players.map((player) => (
                <li key={player.id} className={player.id === room.me?.id ? 'me' : ''}>
                  <div className="room-player-main">
                    <strong>
                      {player.id === room.ownerId && <Crown weight="fill" size={13} className="room-host-icon" />}
                      {player.name}{player.id === room.me?.id ? '（我）' : ''}
                    </strong>
                    <span>
                      {inLobby
                        ? (player.id === room.me?.id
                          ? (player.chosenCity ? `开局城市：${player.chosenCity}` : '还没有选择开局城市')
                          : (player.chosenCity ? '已选定开局城市（盲选保密）' : '正在选择开局城市'))
                        : `第 ${player.week} 周 · 现金 ${money(player.cash)}${player.chosenCity ? ` · 基地 ${player.chosenCity}` : ''}`}
                    </span>
                  </div>
                  <span className={`room-player-pill ${player.gameOver ? 'over' : player.ready ? 'ready' : 'playing'}`}>
                    {player.gameOver ? '已出局' : player.ready ? (inLobby ? '已准备' : '已提交') : (inLobby ? '准备中' : '操作中')}
                  </span>
                </li>
              ))}
            </ul>

            {inLobby && (
              <div className="room-lobby-actions">
                <button
                  className={room.me?.ready ? 'secondary-action' : 'primary-button'}
                  disabled={busy || (!room.me?.ready && !myCity)}
                  onClick={() => void run(() => onSetReady(!room.me?.ready))}
                >
                  {room.me?.ready ? '取消准备' : myCity ? `以 ${myCity} 为基地，准备！` : '先选择开局城市'}
                </button>
                {isOwner ? (
                  <button className="primary-button" disabled={busy || !allReady} onClick={() => void run(onStartGame)}>
                    {allReady ? '开始游戏' : `等待所有玩家准备（${readyCount}/${room.players.length}）`}
                  </button>
                ) : (
                  <p className="room-settings-readonly">所有人准备好后，由房主开始游戏。</p>
                )}
              </div>
            )}

            {confirmLeave ? (
              <div className="settlement-actions">
                <button className="secondary-action" onClick={() => setConfirmLeave(false)}>取消</button>
                <button className="primary-button danger" disabled={busy} onClick={() => void run(async () => { await onLeave(); onClose(); })}>确认离开</button>
              </div>
            ) : (
              <button className="secondary-action room-leave-button" onClick={() => setConfirmLeave(true)}>
                <DoorOpen weight="bold" size={16} /> 离开房间，回到单机模式
              </button>
            )}
            {error && <p className="room-error">{error}</p>}
            {inLobby && room.me?.ready && !isOwner && (
              <p className="room-ready-hint"><CheckCircle weight="fill" size={14} /> 已准备，等待房主开始游戏…</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
